import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

import React, { useContext, useState } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { AccountResponseDTO, IsActiveEnum } from '_lib/api/apiTypes.shim';
import { ActionBar } from '_lib/components/ActionBar/ActionBar';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Button } from '_lib/components/Button/Button';
import { Divider } from '_lib/components/Divider/Divider';
import { FlexBox, FlexBoxProps } from '_lib/components/FlexBox/FlexBox';
import { List } from '_lib/components/List/List';
import { activeEnumOptions, SelectFilter } from '_lib/components/SelectFilter/SelectFilter';
import { Tab } from '_lib/components/Tab/Tab';
import { Tabs } from '_lib/components/Tabs/Tabs';

import { Typography } from '_lib/components/Typography/Typography';

import { toSxArray } from '_lib/utils/helpers';

import { appRoutePaths, getFullAppRoutePath } from '../Nav/nav.types';
import { UserListItem } from '../User/UserListItem';

import { useAccountUserCreateUpdateDialog } from './hooks/AccountUserCreateUpdateDialogHook';

type AccountUserTabsProps = {
  account?: AccountResponseDTO;
  accountId: string;
  renderWithActionBar?: boolean;
} & FlexBoxProps;

export const AccountUserTabs = (props: AccountUserTabsProps) => {
  const { account, accountId, renderWithActionBar, sx, ...flexBoxProps } = props;
  const [tabIndex, setTabIndex] = useState(0);

  const api = useContext(ApiContext);

  const accountUserDeleteMutation = api.accountUsers.useDelete();
  const accountUserReactivateMutation = api.accountUsers.useReactivate();

  const accountUserCreateUpdateDialog = useAccountUserCreateUpdateDialog();

  const accountUsersQuery = api.accountUsers.useUnpagedList({
    params: {
      accountId,
      isActive: tabIndex === 0 ? IsActiveEnum.True : tabIndex === 1 ? IsActiveEnum.False : IsActiveEnum.Any
    }
  });

  return (
    <FlexBox
      sx={[{ flexDirection: 'column', width: '100%', overflow: 'hidden', position: 'relative' }, ...toSxArray(sx)]}
      {...flexBoxProps}>
      {!renderWithActionBar && (
        <Tabs value={tabIndex} onChange={(_event, newValue: number) => setTabIndex(newValue)}>
          <Tab key={0} label='ACTIVE' />
          <Tab key={1} label='INACTIVE' />
        </Tabs>
      )}
      {renderWithActionBar && (
        <ActionBar
          actions={
            <SelectFilter
              label='Active/Inactive'
              initialKey={IsActiveEnum.True}
              options={activeEnumOptions}
              onChange={(selectedKey) =>
                selectedKey === IsActiveEnum.True
                  ? setTabIndex(0)
                  : selectedKey === IsActiveEnum.False
                    ? setTabIndex(1)
                    : setTabIndex(-1)
              }
              sx={{ width: '125px' }}
            />
          }
        />
      )}
      <Divider />
      <FlexBox
        sx={{
          minHeight: '72px',
          position: 'relative',
          flexDirection: 'column',
          overflow: 'auto'
        }}>
        <BackdropSpinner
          sx={{ alignSelf: 'baseline', paddingTop: 2 }}
          open={
            accountUsersQuery.isFetching ||
            accountUserDeleteMutation.isPending ||
            accountUserReactivateMutation.isPending
          }
        />
        {!accountUsersQuery.isFetching && !accountUsersQuery.data?.length && (
          <FlexBox sx={{ justifyContent: 'center' }}>
            <Typography variant='subtitle1' text='No Account Users Found' sx={{ paddingTop: 3 }} />
          </FlexBox>
        )}
        {!accountUsersQuery.isFetching && !!accountUsersQuery.data?.length && (
          <List sx={{ padding: 0 }}>
            {accountUsersQuery.data.map((accountUser) => (
              <UserListItem
                key={accountUser.id}
                disabled={account?.isActive === false}
                user={accountUser}
                href={`${getFullAppRoutePath(appRoutePaths.AccountUser)}/${accountUser.id}`}
                onClick={async (accountUser) => {
                  await accountUserCreateUpdateDialog.show({
                    account,
                    accountUserId: accountUser.id
                  });
                }}
                onDelete={async (accountUser) => {
                  await accountUserDeleteMutation.deleteItem({ id: accountUser.id });
                }}
                onReactivate={async (accountUser) => {
                  await accountUserReactivateMutation.reactivate({ id: accountUser.id });
                }}
              />
            ))}
          </List>
        )}
      </FlexBox>
      <Divider />
      <FlexBox sx={{ width: '100%', height: '48px' }}>
        <Button
          startIcon={<AddCircleOutlineIcon />}
          disabled={account?.isActive === false}
          text='User'
          variant='contained'
          sx={{ textTransform: 'none', marginLeft: 'auto', marginY: 1, marginRight: 1 }}
          onClick={async () => {
            await accountUserCreateUpdateDialog.show({
              account
            });
          }}
        />
      </FlexBox>
      {accountUserCreateUpdateDialog.element}
    </FlexBox>
  );
};
